import { extractPassengers, nameKey, normalizeClock } from "./heuristic-extract";
import { formatPhoneForDisplay } from "./phone-format";
import { isTechnicalWarning } from "./findings-display";

const asString = (value) => (value == null ? "" : String(value).replace(/\s+/g, " ").trim());

// Coordinates a row already carries (sample roster, a file with lat/lng columns); mirrors the backend rule.
export const asCoordinates = (lat, lng) => {
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  if (Math.abs(la) > 90 || Math.abs(ln) > 180 || (la === 0 && ln === 0)) return null;
  return { lat: la, lng: ln };
};

const uniqueSchools = (passengers, extras = []) => {
  const list = [];
  const seen = new Set();
  const add = (name, address, coords) => {
    const trimmed = asString(name);
    if (!trimmed) return;
    const key = trimmed.toLocaleLowerCase("tr");
    if (seen.has(key)) {
      const existing = list.find((school) => school.name.toLocaleLowerCase("tr") === key);
      if (existing && !existing.address && address) existing.address = asString(address);
      if (existing && existing.lat == null && coords) {
        existing.lat = coords.lat;
        existing.lng = coords.lng;
      }
      return;
    }
    seen.add(key);
    const entry = { name: trimmed, address: asString(address) };
    if (coords) {
      entry.lat = coords.lat;
      entry.lng = coords.lng;
    }
    list.push(entry);
  };
  extras.forEach((school) => add(school?.name || school, school?.address, asCoordinates(school?.lat, school?.lng)));
  passengers.forEach((passenger) => add(passenger.schoolName, passenger.schoolAddress));
  return list;
};

export const missingForPassenger = (passenger) => {
  const missing = [];
  if (!passenger.name) missing.push("name");
  if (!passenger.schoolName) missing.push("school");
  if (!passenger.addressGetOn && !passenger.addressGetOff) missing.push("address");
  if (!passenger.parents?.length || !passenger.parents.some((parent) => parent.name)) missing.push("parent");
  if (!passenger.parents?.some((parent) => parent.phone) && !passenger.phone) missing.push("phone");
  return missing;
};

const applyWorkbookSchool = (passengers, schools) => {
  if (!schools.length) return passengers;
  const fallback = schools[0];
  return passengers.map((passenger) => {
    if (passenger.schoolName) return passenger;
    return { ...passenger, schoolName: fallback.name, schoolAddress: fallback.address || "" };
  });
};

const asLeg = (leg, fallback) => {
  if (!leg || typeof leg !== "object") return null;
  return {
    startTime: normalizeClock(leg.startTime),
    endTime: normalizeClock(leg.endTime),
    plate: asString(leg.plate || fallback.plate).toUpperCase(),
    driver: asString(leg.driver || fallback.driver),
    driverPhone: asString(leg.driverPhone || fallback.driverPhone),
    attendant: asString(leg.attendant || fallback.attendant),
  };
};

const asRouteList = (value) => {
  if (!Array.isArray(value)) return [];
  const byKey = new Map();
  const list = [];
  value.forEach((route, index) => {
    const name = asString(route?.name);
    if (!name) return;
    const key = name.toLocaleLowerCase("tr");
    const unknown = route?.pickup == null && route?.dropoff == null && !route?.legs;
    const base = { plate: route?.plate, driver: route?.driver, driverPhone: route?.driverPhone, attendant: route?.attendant };
    const legs = {
      pickup: asLeg(route?.legs?.pickup, base) || (unknown || route?.pickup ? asLeg({}, base) : null),
      dropoff: asLeg(route?.legs?.dropoff, base) || (unknown || route?.dropoff ? asLeg({}, base) : null),
    };
    const existing = byKey.get(key);
    if (existing) {
      if (!existing.legs.pickup && legs.pickup) existing.legs.pickup = legs.pickup;
      if (!existing.legs.dropoff && legs.dropoff) existing.legs.dropoff = legs.dropoff;
      existing.pickup = Boolean(existing.legs.pickup);
      existing.dropoff = Boolean(existing.legs.dropoff);
      return;
    }
    const entry = {
      rowNumber: Number(route?.rowNumber) || index + 2,
      name,
      driver: asString(route?.driver),
      driverPhone: asString(route?.driverPhone),
      plate: asString(route?.plate).toUpperCase(),
      attendant: asString(route?.attendant),
      capacity: asString(route?.capacity),
      schoolName: asString(route?.schoolName),
      routeKind: asString(route?.routeKind),
      sheetName: asString(route?.sheetName),
      legs,
      pickup: Boolean(legs.pickup),
      dropoff: Boolean(legs.dropoff),
    };
    byKey.set(key, entry);
    list.push(entry);
  });
  return list;
};

export const routeLegFor = (routes, name, direction) => {
  const key = nameKey(name);
  if (!key) return null;
  const route = (routes || []).find((entry) => nameKey(entry.name) === key);
  return route?.legs?.[direction] || null;
};

export const duplicateRowNumbers = (passengers = []) => {
  const seen = new Map();
  const duplicates = new Set();
  passengers.forEach((passenger) => {
    if (!passenger.name) return;
    const key = nameKey(passenger.name);
    if (seen.has(key)) duplicates.add(passenger.rowNumber);
    else seen.set(key, passenger.rowNumber);
  });
  return duplicates;
};

export const normalizeExtraction = (raw = {}, meta = {}) => {
  let passengers = (Array.isArray(raw.passengers) ? raw.passengers : [])
    .map((row, index) => {
      const addressGetOn = asString(row?.addressGetOn || row?.address);
      const coordinates = asCoordinates(row?.lat, row?.lng);
      const passenger = {
        rowNumber: Number(row?.rowNumber) || index + 2,
        lat: coordinates ? coordinates.lat : null,
        lng: coordinates ? coordinates.lng : null,
        name: asString(row?.name),
        identifier: asString(row?.identifier),
        grade: asString(row?.grade || row?.level),
        schoolName: asString(row?.schoolName || row?.school),
        schoolAddress: asString(row?.schoolAddress),
        addressGetOn,
        addressGetOff: asString(row?.addressGetOff) || addressGetOn,
        pickupRouteName: asString(row?.pickupRouteName),
        dropoffRouteName: asString(row?.dropoffRouteName),
        phone: asString(row?.phone),
        email: asString(row?.email),
        parents: Array.isArray(row?.parents)
          ? row.parents
              .map((parent) => ({
                name: asString(parent?.name),
                phone: asString(parent?.phone),
                email: asString(parent?.email),
              }))
              .filter((parent) => parent.name || parent.phone || parent.email)
          : [],
        notes: asString(row?.notes),
      };
      passenger.missing = missingForPassenger(passenger);
      return passenger;
    })
    .filter((passenger) => meta.keepEmpty || passenger.name || passenger.addressGetOn || passenger.parents.length);

  const schoolsFromModel = (Array.isArray(raw.schools) ? raw.schools : []).map((school) => {
    const coords = asCoordinates(school?.lat, school?.lng);
    return {
      name: asString(school?.name || school),
      address: asString(school?.address),
      ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
    };
  });
  const workbookSchools = Array.isArray(meta.schoolsFromWorkbook) ? meta.schoolsFromWorkbook : [];
  passengers = applyWorkbookSchool(
    passengers,
    [...workbookSchools, ...schoolsFromModel].filter((school) => school.name),
  );
  passengers = passengers.map((passenger) => ({ ...passenger, missing: missingForPassenger(passenger) }));

  const named = passengers.filter((passenger) => passenger.name);
  const schools = uniqueSchools(passengers, [...workbookSchools, ...schoolsFromModel]);
  const routes = asRouteList(raw.routes && raw.routes.length ? raw.routes : meta.routeList);
  const routeNames = new Set();
  const pickupNames = new Set();
  const dropoffNames = new Set();
  named.forEach((passenger) => {
    if (passenger.pickupRouteName) {
      routeNames.add(passenger.pickupRouteName.toLocaleLowerCase("tr"));
      pickupNames.add(passenger.pickupRouteName.toLocaleLowerCase("tr"));
    }
    if (passenger.dropoffRouteName) {
      routeNames.add(passenger.dropoffRouteName.toLocaleLowerCase("tr"));
      dropoffNames.add(passenger.dropoffRouteName.toLocaleLowerCase("tr"));
    }
  });
  const duplicates = duplicateRowNumbers(named);

  return {
    sheetKind: asString(raw.sheetKind) || "school_transport_roster",
    language: asString(raw.language),
    schools,
    passengers,
    routes,
    unmappedColumns: Array.isArray(raw.unmappedColumns) ? raw.unmappedColumns.map(asString).filter(Boolean) : [],
    warnings: (Array.isArray(raw.warnings) ? raw.warnings.map(asString).filter(Boolean) : []).filter(
      (warning) => !isTechnicalWarning(warning),
    ),
    stats: {
      rowCount: Number(meta.rowCount) || passengers.length,
      passengerCount: named.length,
      schoolCount: schools.length,
      missing: {
        passengerName: passengers.filter((passenger) => passenger.missing.includes("name")).length,
        address: named.filter((passenger) => passenger.missing.includes("address")).length,
        school: named.filter((passenger) => passenger.missing.includes("school")).length,
        parentName: named.filter((passenger) => passenger.missing.includes("parent")).length,
        parentPhone: named.filter((passenger) => passenger.missing.includes("phone")).length,
      },
      parentSlots: Math.max(0, ...named.map((passenger) => passenger.parents.length), 0),
      namedRouteCount: named.filter((passenger) => passenger.pickupRouteName || passenger.dropoffRouteName).length,
      distinctRouteNameCount: routeNames.size,
      pickupRouteNameCount: pickupNames.size,
      dropoffRouteNameCount: dropoffNames.size,
      pickupAssignedCount: named.filter((passenger) => passenger.pickupRouteName).length,
      dropoffAssignedCount: named.filter((passenger) => passenger.dropoffRouteName).length,
      routeListCount: routes.length,
      routeListPickupCount: routes.filter((route) => route.pickup).length,
      routeListDropoffCount: routes.filter((route) => route.dropoff).length,
      routeListLegCount: routes.reduce((sum, route) => sum + Number(route.pickup) + Number(route.dropoff), 0),
      routeListWithoutPassengers: routes.filter((route) => !routeNames.has(route.name.toLocaleLowerCase("tr"))).length,
      duplicateNameCount: duplicates.size,
    },
    interpreter: meta.interpreter || raw.interpreter || "heuristic",
  };
};

export const applyOperatorSchool = (extraction, { schoolName, schoolAddress, schoolDecision } = {}) => {
  const name = asString(schoolName);
  const address = asString(schoolAddress);
  if (schoolDecision === "later" || !name) return extraction;
  const overwrite = (extraction?.schools || []).filter((school) => school.name).length <= 1;
  const passengers = (extraction?.passengers || []).map((passenger) => ({
    ...passenger,
    schoolName: overwrite ? name : passenger.schoolName || name,
    schoolAddress: overwrite ? address || passenger.schoolAddress : passenger.schoolAddress || address,
  }));
  return normalizeExtraction(
    { ...extraction, passengers, schools: [{ name, address }, ...(extraction.schools || [])] },
    {
      interpreter: extraction?.interpreter,
      rowCount: extraction?.stats?.rowCount,
      schoolsFromWorkbook: [{ name, address }],
      keepEmpty: true,
    },
  );
};

// Operator edits: patch one row, drop one row, or drop many. Rows are re-normalized but never
// silently discarded — an emptied row stays visible until the operator removes it.
export const patchPassenger = (extraction, rowNumber, patch) => {
  const passengers = (extraction?.passengers || []).map((passenger) => {
    if (passenger.rowNumber !== rowNumber) return passenger;
    const next = { ...passenger, ...patch };
    if (patch.parents) next.parents = patch.parents;
    return next;
  });
  return normalizeExtraction(
    { ...extraction, passengers },
    { interpreter: extraction?.interpreter, rowCount: extraction?.stats?.rowCount, keepEmpty: true },
  );
};

export const removePassengers = (extraction, rowNumbers) => {
  const drop = new Set(rowNumbers);
  const removed = (extraction?.passengers || []).filter((passenger) => drop.has(passenger.rowNumber));
  const passengers = (extraction?.passengers || []).filter((passenger) => !drop.has(passenger.rowNumber));
  return {
    extraction: normalizeExtraction(
      { ...extraction, passengers },
      { interpreter: extraction?.interpreter, rowCount: extraction?.stats?.rowCount, keepEmpty: true },
    ),
    removed,
  };
};

export const restorePassengers = (extraction, removed) => {
  const passengers = [...(extraction?.passengers || []), ...(removed || [])].sort((a, b) => a.rowNumber - b.rowNumber);
  return normalizeExtraction(
    { ...extraction, passengers },
    { interpreter: extraction?.interpreter, rowCount: extraction?.stats?.rowCount, keepEmpty: true },
  );
};

export const extractionToPreview = (extraction) => {
  const passengers = extraction?.passengers || [];
  const students = passengers.map((passenger, index) => ({
    id: `row-${passenger.rowNumber || index + 1}`,
    rowNumber: passenger.rowNumber || index + 2,
    name: passenger.name || `Row ${passenger.rowNumber || index + 2}`,
    school: passenger.schoolName || "",
    address: passenger.addressGetOn || passenger.addressGetOff || "",
    parent: passenger.parents?.[0]?.name || "",
    phone: formatPhoneForDisplay(passenger.parents?.[0]?.phone || passenger.phone),
    email: passenger.parents?.[0]?.email || passenger.email || "",
    grade: passenger.grade || "",
    pickupRouteName: passenger.pickupRouteName || "",
    dropoffRouteName: passenger.dropoffRouteName || "",
    missing: passenger.missing || missingForPassenger(passenger),
    recognized: Boolean(passenger.name),
  }));

  const schoolMap = new Map();
  students.forEach((student) => {
    const key = student.school;
    if (!key) return;
    if (!schoolMap.has(key)) schoolMap.set(key, { name: key, count: 0, missingAddress: 0 });
    const entry = schoolMap.get(key);
    entry.count += 1;
    if (!student.address) entry.missingAddress += 1;
  });

  return {
    students,
    schools: Array.from(schoolMap.values()),
    missingNameCount: students.filter((student) => student.missing.includes("name")).length,
    missingSchoolCount: students.filter((student) => student.missing.includes("school")).length,
    missingAddressCount: students.filter((student) => student.missing.includes("address")).length,
    missingParentCount: students.filter((student) => student.missing.includes("parent")).length,
    missingPhoneCount: students.filter((student) => student.missing.includes("phone")).length,
    recognizedCount: students.filter((student) => student.recognized).length,
    interpreter: extraction?.interpreter,
    warnings: extraction?.warnings || [],
  };
};

export const extractionFromParsedTable = (parsed) => {
  const rosterSheets =
    parsed?.rosterSheets?.length > 0
      ? parsed.rosterSheets
      : parsed?.headers && parsed?.rows && !(parsed?.routeSheets?.length > 0)
        ? [{ headers: parsed.headers, rows: parsed.rows }]
        : [];
  const passengers = [];
  const unmapped = [];
  rosterSheets.forEach((sheet) => {
    const extracted = extractPassengers(sheet.headers || [], sheet.rows || []);
    passengers.push(...extracted.passengers);
    unmapped.push(...(extracted.roles?.unmapped || []));
  });
  return normalizeExtraction(
    {
      passengers,
      schools: parsed?.schoolsFromWorkbook || [],
      routes: parsed?.routeList || [],
      unmappedColumns: unmapped,
      interpreter: "heuristic",
    },
    {
      interpreter: "heuristic",
      rowCount: parsed?.rows?.length || passengers.length,
      schoolsFromWorkbook: parsed?.schoolsFromWorkbook || [],
      routeList: parsed?.routeList || [],
    },
  );
};

// Manual column mapping — the fallback when headers are so unusual that nothing was recognized.
export const MANUAL_FIELDS = [
  { id: "name", label: "Student name" },
  { id: "firstName", label: "First name" },
  { id: "lastName", label: "Last name" },
  { id: "school", label: "School" },
  { id: "address", label: "Address" },
  { id: "parent", label: "Parent name" },
  { id: "phone", label: "Parent phone" },
  { id: "email", label: "Email" },
  { id: "grade", label: "Grade" },
  { id: "pickupRoute", label: "Morning service" },
  { id: "dropoffRoute", label: "Afternoon service" },
];

export const extractionFromMapping = (parsed, mapping = {}) => {
  const headers = parsed?.headers || [];
  const rows = parsed?.rows || [];
  const cell = (row, field) => {
    const index = mapping?.[field];
    if (index == null || index === "") return "";
    return asString(row[Number(index)]);
  };
  const passengers = rows
    .map((row, index) => {
      const name = cell(row, "name") || [cell(row, "firstName"), cell(row, "lastName")].filter(Boolean).join(" ");
      const parentName = cell(row, "parent");
      const parentPhone = cell(row, "phone");
      const parentEmail = cell(row, "email");
      const parents = parentName || parentPhone || parentEmail ? [{ name: parentName, phone: parentPhone, email: parentEmail }] : [];
      return {
        rowNumber: index + 2,
        name,
        schoolName: cell(row, "school"),
        addressGetOn: cell(row, "address"),
        grade: cell(row, "grade"),
        pickupRouteName: cell(row, "pickupRoute"),
        dropoffRouteName: cell(row, "dropoffRoute"),
        parents,
      };
    })
    .filter((passenger) => passenger.name || passenger.addressGetOn || passenger.parents.length);
  return normalizeExtraction(
    {
      passengers,
      schools: parsed?.schoolsFromWorkbook || [],
      routes: parsed?.routeList || [],
      unmappedColumns: headers.filter((_, index) => !Object.values(mapping || {}).some((value) => Number(value) === index)),
      interpreter: "manual",
    },
    {
      interpreter: "manual",
      rowCount: rows.length,
      schoolsFromWorkbook: parsed?.schoolsFromWorkbook || [],
      routeList: parsed?.routeList || [],
    },
  );
};
